import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, Space, Typography, Divider, message } from 'antd';
import { SaveOutlined, MessageOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

export default function SMSSettingsManager() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, message, details }

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getSMSSettings();
      if (res.data.success) {
        const s = res.data.settings;
        setHasApiKey(s.hasApiKey);
        form.setFieldsValue({
          apiKey: '',
          sender: s.sender || '',
          waitlistOtpTemplateId: s.waitlistOtpTemplateId || 590295,
          waitlistSuccessTemplateId: s.waitlistSuccessTemplateId || 372782,
          invitationTemplateId: s.invitationTemplateId || ''
        });
      }
    } catch {
      message.error('خطا در بارگذاری تنظیمات پیامک');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const res = await adminAPI.updateSMSSettings(values);
      if (res.data.success) {
        message.success('تنظیمات پیامک با موفقیت ذخیره شد');
        loadSettings();
      } else {
        message.error(res.data.message || 'خطا در ذخیره');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در اتصال');
    } finally {
      setSaving(false);
    }
  };

  // ارسال یک پیامک آزمایشی با همین apiKey/sender ذخیره‌شده — مستقل از هر
  // دعوت‌نامه یا ورودی لیست انتظار خاصی. اگر اینجا هم خطای «شماره اشتباه»
  // دیدید، مطمئن باشید مشکل از شماره‌ی گیرنده‌ی این تست نیست چون همین الان
  // آن را خودتان وارد کرده‌اید — یعنی مشکل قطعاً از تنظیمات خط ارسال است.
  const handleTestSend = async () => {
    if (!testPhone || testPhone.trim().length < 5) {
      message.warning('شماره‌ی گیرنده برای تست را وارد کنید');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminAPI.testSMSConfig(testPhone.trim());
      setTestResult(res.data);
      if (res.data.success) {
        message.success('پیامک آزمایشی ارسال شد — گیرنده باید آن را دریافت کرده باشد');
      } else {
        message.error(res.data.message || 'ارسال آزمایشی ناموفق بود');
      }
    } catch (e) {
      const data = e?.response?.data;
      setTestResult(data || { success: false, message: 'خطا در اتصال به سرور' });
      message.error(data?.message || 'خطا در اتصال به سرور');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <Alert
        type="info"
        showIcon
        icon={<MessageOutlined />}
        style={{ marginBottom: 20 }}
        message="تنظیمات سرویس پیامک sms.ir"
        description={
          <span>
            کلید API و شماره فرستنده خود را از پنل <a href="https://app.sms.ir" target="_blank" rel="noreferrer">sms.ir</a> دریافت کنید.
            {hasApiKey && <Text type="success" style={{ marginRight: 8 }}>✓ کلید API ذخیره شده است</Text>}
          </span>
        }
      />

      <Card
        loading={loading}
        title={
          <Space>
            <MessageOutlined />
            <span>پیکربندی sms.ir</span>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          style={{ fontFamily: "'Vazirmatn', sans-serif" }}
        >
          <Form.Item
            name="apiKey"
            label="کلید API (X-API-KEY)"
            help={
              hasApiKey
                ? 'کلید API ذخیره‌شده وجود دارد. برای تغییر، کلید جدید وارد کنید. در غیر این‌صورت خالی بگذارید.'
                : 'کلید API را از پنل sms.ir → تنظیمات → توکن دریافت کنید'
            }
          >
            <Input.Password
              placeholder={hasApiKey ? '••••••••••••• (بدون تغییر)' : 'کلید API خود را وارد کنید'}
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Form.Item
            name="sender"
            label="شماره فرستنده (خط)"
            rules={[{ required: false }]}
            help="شماره خطی که پیامک از آن ارسال می‌شود"
          >
            <Input
              placeholder="مثال: 3000505"
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Divider orientation="right">
            <Space>
              <InfoCircleOutlined />
              <span>شناسه قالب‌های تأیید هویت</span>
            </Space>
          </Divider>

          <Form.Item
            name="waitlistOtpTemplateId"
            label="شناسه قالب OTP (کد تأیید)"
            help="Template ID قالب کد یکبار مصرف — متغیر: #OTP#"
          >
            <Input
              type="number"
              placeholder="590295"
              style={{ fontFamily: 'monospace', direction: 'ltr', width: 180 }}
            />
          </Form.Item>

          <Form.Item
            name="waitlistSuccessTemplateId"
            label="شناسه قالب تأیید ثبت‌نام لیست انتظار"
            help="Template ID قالب پیام موفقیت — متغیر: #FIRSTNAME#"
          >
            <Input
              type="number"
              placeholder="372782"
              style={{ fontFamily: 'monospace', direction: 'ltr', width: 180 }}
            />
          </Form.Item>

          <Form.Item
            name="invitationTemplateId"
            label="شناسه قالب ارسال دعوت‌نامه (اختیاری)"
            help="Template ID قالب دعوت‌نامه (در صورت خالی بودن از متن پیش‌فرض/ثبت‌شده در پنل استفاده می‌شود) — متغیرها: #FIRSTNAME# و #CODE# و #LINK#"
          >
            <Input
              type="number"
              placeholder="مثال: 123456"
              style={{ fontFamily: 'monospace', direction: 'ltr', width: 180 }}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              ذخیره تنظیمات
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <Space>
            <MessageOutlined />
            <span>تست ارسال پیامک</span>
          </Space>
        }
        style={{ marginTop: 20 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          یک پیامک آزمایشی با تنظیمات ذخیره‌شده‌ی بالا (کلید API و شماره فرستنده) به شماره‌ای که خودتان وارد می‌کنید ارسال می‌شود —
          مستقل از هر دعوت‌نامه یا لیست انتظار. اگر اینجا هم خطا بگیرید، چون شماره‌ی گیرنده را خودتان همین الان درست وارد کرده‌اید،
          مشکل قطعاً از تنظیمات «شماره فرستنده / خط ارسال» یا کلید API است، نه شماره‌ی گیرنده.
        </Text>
        <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
          <Input
            placeholder="09123456789"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            style={{ fontFamily: 'monospace', direction: 'ltr' }}
          />
          <Button type="primary" loading={testing} onClick={handleTestSend}>
            ارسال پیامک آزمایشی
          </Button>
        </Space.Compact>

        {testResult && (
          <Alert
            style={{ marginTop: 16 }}
            type={testResult.success ? 'success' : 'error'}
            showIcon
            message={testResult.success ? 'ارسال موفق' : 'ارسال ناموفق'}
            description={
              <div>
                <div>{testResult.message}</div>
                {testResult.details?.raw && (
                  <pre style={{ marginTop: 8, fontSize: 12, direction: 'ltr', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(testResult.details.raw, null, 2)}
                  </pre>
                )}
              </div>
            }
          />
        )}
      </Card>
    </div>
  );
}
